import React, { useState } from "react";
import classnames from "classnames";

import { TextLink } from "components/actions/Links";
import ShareModal from "components/modals/ShareModal";
import {
  generateProcurementUrl,
  getFrontendQualification,
  NoticeFilters,
  recordDetailsUrl,
} from "components/notices/utils";
import Alert from "components/ui/alert/Alert";
import NoticeStage from "lib/core_components/NoticeStage";
import { TextHighlighter } from "lib/core_components/TextHighlighter";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useNoticeListsForRecord } from "lib/hooks/api/notices/lists/useNoticeListsForRecord";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { EventNames, useTracking } from "lib/tracking";
import { getSignalTypes } from "lib/types/models";
import { assertDefined, isOpenEndedDate } from "lib/utils";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { NoticesStages as Stages, NoticeTimelineStage } from "../../lib/types/graphQLEnums";
import { NoticeActions } from "../notices/NoticeActions";
import { NoticeHeaderDropdown } from "./NoticeHeaderDropdown";
import { NoticeSeenBy } from "./SeenBy";

import css from "./NoticeHeader.module.scss";

function getRecordDate(notice: NoticeDetails) {
  switch (notice.stage) {
    case Stages.Awarded:
      return notice.expiryDate;
    case Stages.Open:
    case Stages.Closed:
      return notice.closeDate;
    default:
      return notice.publishDate;
  }
}

function getRelativeTimeLabel(stage: Stages, isFuture: boolean) {
  switch (stage) {
    case Stages.Awarded:
      return isFuture ? "Expiring" : "Expired";
    case Stages.Open:
    case Stages.Closed:
      return isFuture ? "Closing" : "Closed";
    default:
      return "Published";
  }
}

function getEmptyDateLabel(stage: Stages) {
  switch (stage) {
    case Stages.Awarded:
      return "expiry";
    case Stages.Open:
    case Stages.Closed:
      return "close";
    default:
      return "publish";
  }
}

function getLatestStage(timeline: NoticeDetails["timeline"]): NoticeTimelineStage | null {
  const latestAward = timeline?.award.find(
    (award) => award.estimated === false && award.publishedDate !== null,
  );
  if (latestAward) {
    return NoticeTimelineStage.Award;
  }
  if (timeline?.tender && timeline.tender?.estimated === false) {
    return NoticeTimelineStage.Tender;
  }
  if (timeline?.preTender) {
    return NoticeTimelineStage.PreTender;
  }
  // Should be impossible with the current logic, but just in case
  return null;
}

export function getRelativeTimeFromNow(notice: NoticeDetails): string | null {
  assertDefined(notice.stage);

  const recordDate = getRecordDate(notice);

  if (!recordDate) {
    return `Unknown ${getEmptyDateLabel(notice.stage)} date`;
  }

  const dateObj = new Date(recordDate);
  const isFuture = dateObj.getTime() > Date.now();
  const label = getRelativeTimeLabel(notice.stage, isFuture);

  if (isOpenEndedDate(dateObj)) {
    return "Open-ended";
  }

  const timeFromNow = toHumanRelativeDateFromNow(recordDate);
  return `${label} ${timeFromNow}`;
}

type NoticeHeaderProps = {
  notice: NoticeDetails;
  headerHighlights: string[];
  trackingContextName:
    | "Title header"
    | "Record preview slide-out"
    | "In-table row"
    | "In-table dropdown";
  disableTitleLink?: boolean;
  showStage?: boolean;
  headerClassName?: string;
  scrollClassName?: string;
  isTopOfPageVisible?: boolean;
  // Filters used here to translate active filters to eventually
  // become a search request for highlighting API in notice details page
  filters?: NoticeFilters;
};

export default function NoticeHeader({
  notice,
  filters,
  headerHighlights,
  trackingContextName,
  disableTitleLink,
  showStage = true,
  headerClassName,
  scrollClassName,
  isTopOfPageVisible,
}: NoticeHeaderProps) {
  assertDefined(notice.stage);
  const { data: providerTypeResponse } = useFindIntegrationProvider();
  const providerType = providerTypeResponse?.providerType;
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const { logEvent } = useTracking();

  // TODO: Load this data from graphql in the notice object
  const { data: noticeLists } = useNoticeListsForRecord(notice.guid);

  const onOpenChange = (open: boolean) => {
    setShareModalOpen(open);
    if (open) {
      logEvent(EventNames.shareEmailModalOpen, { "Record GUIDs": [notice.guid] });
    }
  };

  const headerClass = classnames(
    headerClassName,
    scrollClassName && !isTopOfPageVisible && scrollClassName,
  );

  const timeFromNowLabel = getRelativeTimeFromNow(notice);

  const latestStageForNotice = getLatestStage(notice.timeline);

  return (
    <div className={headerClass}>
      <div className={css.headerColumn}>
        <div className={css.recordHeaderContent}>
          <div className={css.titleContainer}>
            {showStage && (
              <div className={css.stage}>
                <NoticeStage stage={notice.stagev2 ?? undefined} />
                <div className={css.date}>
                  •<div>{timeFromNowLabel}</div>
                </div>
              </div>
            )}
            <div aria-label="Record link">
              <TextLink
                className={css.title}
                targetType="new-tab"
                to={recordDetailsUrl(notice.guid, filters)}
                disabled={disableTitleLink}
              >
                <TextHighlighter content={notice.name ?? ""} wordsToHighlight={headerHighlights} />
              </TextLink>
              <NoticeSeenBy
                seenByUsers={notice.seenByUsers}
                displayNumber={true}
                className={css.seenBy}
              />
            </div>
          </div>

          {!window.guestUser && (
            <div className={css.actions}>
              <NoticeActions
                notice={{
                  id: notice.guid,
                  name: notice.name ?? "",
                  value: notice.value ?? undefined,
                  score: notice.score,
                  signalTypes: getSignalTypes([]),
                  procurementStage: {
                    id: notice.procurementStage.id,
                    stage: notice.procurementStage.stage
                      ? notice.procurementStage.stage.toString()
                      : "",
                    qualification: notice.qualification
                      ? getFrontendQualification(notice.qualification)
                      : undefined,
                  },
                  stage: notice.stage,
                  stageV2: notice.stagev2 || undefined,
                  closeDate: notice.closeDate || undefined,
                  expiryDate: notice.expiryDate || undefined,
                  buyers: notice.buyers.map((buyer) => buyer.guid),
                  suppliers: Array.from(
                    new Set(
                      notice.lots.flatMap(
                        (lot) => lot.suppliers?.map((supplier) => supplier.guid) || [],
                      ),
                    ),
                  ),
                  listCount: noticeLists?.length,
                }}
                contextSource={trackingContextName}
              />
              <NoticeHeaderDropdown
                hasCrm={!!providerType}
                onShareClick={() => onOpenChange(true)}
                notice={notice}
              />
              <ShareModal
                open={shareModalOpen}
                onOpenChange={onOpenChange}
                record={{
                  guid: notice.guid,
                  signals: notice.signals ?? [],
                  stage: notice.stage,
                  score: notice.score ?? 0,
                }}
              />
            </div>
          )}
        </div>
        {latestStageForNotice && notice.procurementStage.stage !== latestStageForNotice && (
          <Alert type="info">
            This is no longer the latest notice for this process.{" "}
            <TextLink
              to={generateProcurementUrl(
                notice.procurementStage.procurementProcessId,
                latestStageForNotice,
              )}
            >
              Click here to view the most recent notice.
            </TextLink>
          </Alert>
        )}
      </div>
    </div>
  );
}
